import moment from "moment";

function toLatinDigits(value) {
  return String(value ?? "")
    .replace(/[\u0660-\u0669]/g, (digit) =>
      String(digit.charCodeAt(0) - 0x0660)
    )
    .replace(/[\u06F0-\u06F9]/g, (digit) =>
      String(digit.charCodeAt(0) - 0x06f0)
    );
}

export function formatDateForApi(date) {
  if (!date) return "";

  const normalized = toLatinDigits(date);
  const parsed = moment(
    normalized,
    ["YYYY-MM-DD", "YYYY-M-D", moment.ISO_8601],
    true
  );

  if (parsed.isValid()) {
    return parsed.locale("en").format("YYYY-MM-DD");
  }

  const fallback = moment(normalized);
  return fallback.isValid()
    ? fallback.locale("en").format("YYYY-MM-DD")
    : normalized;
}

export function parseDateFromApi(date) {
  if (!date) return null;

  const formatted = formatDateForApi(date);
  return formatted || null;
}

export function resolveFestivalEndDate(endDate, timeSlots = [], type) {
  const parsedEnd = parseDateFromApi(endDate);

  if (type !== "extended") {
    return parsedEnd;
  }

  const slotDates = (timeSlots || [])
    .map((slot) => parseDateFromApi(slot.slot_date))
    .filter(Boolean)
    .sort();

  const latestSlotDate = slotDates[slotDates.length - 1];

  if (!parsedEnd) {
    return latestSlotDate || null;
  }

  if (latestSlotDate && latestSlotDate > parsedEnd) {
    return latestSlotDate;
  }

  return parsedEnd;
}

export function getDateRange(startDate, endDate, type) {
  if (!startDate) return [];

  const start = moment(formatDateForApi(startDate), "YYYY-MM-DD", true);
  if (!start.isValid()) return [];

  if (type === "extended" && endDate) {
    const end = moment(formatDateForApi(endDate), "YYYY-MM-DD", true);
    if (!end.isValid() || end.isBefore(start, "day")) return [];

    const dates = [];
    const current = start.clone();
    while (current.isSameOrBefore(end, "day")) {
      dates.push(current.locale("en").format("YYYY-MM-DD"));
      current.add(1, "day");
    }
    return dates;
  }

  return [start.locale("en").format("YYYY-MM-DD")];
}

export function createDefaultSlot() {
  return {
    id: null,
    start_time: null,
    end_time: null,
    max_tickets: null,
  };
}

export function createDefaultDay(date) {
  return {
    date,
    is_active: true,
    time_slots: [createDefaultSlot()],
  };
}

export function syncSchedules(existing, startDate, endDate, type) {
  const dates = getDateRange(startDate, endDate, type);
  const existingDays = existing || [];
  const existingByDate = {};

  existingDays.forEach((day) => {
    existingByDate[formatDateForApi(day.date)] = day;
  });

  // Limited: one day — when the start date changes, keep the same slots
  // and only move them onto the new date.
  if (
    type === "limited" &&
    dates.length === 1 &&
    existingDays.length === 1 &&
    !existingByDate[dates[0]]
  ) {
    const onlyDay = existingDays[0];
    return [
      {
        ...onlyDay,
        date: dates[0],
        time_slots:
          onlyDay.time_slots?.length > 0
            ? onlyDay.time_slots
            : [createDefaultSlot()],
      },
    ];
  }

  return dates.map((date) => {
    if (existingByDate[date]) {
      return {
        ...existingByDate[date],
        date,
        time_slots:
          existingByDate[date].time_slots?.length > 0
            ? existingByDate[date].time_slots
            : [createDefaultSlot()],
      };
    }
    return createDefaultDay(date);
  });
}

export function parseTimeFromApi(timeStr) {
  if (!timeStr) return null;
  if (typeof timeStr === "object" && timeStr.HH !== undefined) return timeStr;

  const parts = String(timeStr).split(":");
  return {
    HH: parts[0] || "00",
    mm: parts[1] || "00",
    ss: parts[2] || "00",
  };
}

export function formatTimeForApi(time) {
  const normalized = normalizeTimeValue(time);
  return normalized || "";
}

export function normalizeTimeValue(time) {
  if (!time) return null;

  if (typeof time.format === "function") {
    return time.format("HH:mm");
  }

  if (typeof time === "string") {
    const match = toLatinDigits(time).match(/^(\d{1,2}):(\d{2})/);
    if (!match) return null;

    const [, hh, mm] = match;
    return `${String(hh).padStart(2, "0")}:${mm}`;
  }

  if (time.HH !== undefined) {
    const hh = String(time.HH).padStart(2, "0");
    const mm = String(time.mm ?? "00").padStart(2, "0");
    return `${hh}:${mm}`;
  }

  return null;
}

export function timeToSeconds(time) {
  const normalized = normalizeTimeValue(time);
  if (!normalized) return null;

  const match = normalized.match(/^(\d{2}):(\d{2})/);
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);

  if (
    !Number.isInteger(hours) ||
    !Number.isInteger(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }

  return hours * 3600 + minutes * 60;
}

function translateMessage(translate, key, params) {
  return translate(key, params);
}

export function getSlotFieldErrors(slot, translate, options = {}) {
  const { showAll = false, touched = {} } = options;
  const errors = [];
  const startSeconds = timeToSeconds(slot.start_time);
  const endSeconds = timeToSeconds(slot.end_time);
  const startLabel = formatTimeForApi(slot.start_time);
  const endLabel = formatTimeForApi(slot.end_time);
  const shouldValidateStart = showAll || touched.start_time;
  const shouldValidateEnd = showAll || touched.end_time;
  const shouldValidateMax = showAll || touched.max_tickets;

  if (shouldValidateStart && startSeconds === null) {
    errors.push(translateMessage(translate, "MESSAGES.entryStartTimeRequired"));
  }

  if (shouldValidateEnd && endSeconds === null) {
    errors.push(translateMessage(translate, "MESSAGES.entryEndTimeRequired"));
  }

  if (
    shouldValidateMax &&
    (slot.max_tickets === null || slot.max_tickets === "")
  ) {
    errors.push(translateMessage(translate, "MESSAGES.maxTicketsRequired"));
  }

  if (
    startSeconds !== null &&
    endSeconds !== null &&
    startSeconds >= endSeconds
  ) {
    errors.push(
      translateMessage(translate, "MESSAGES.entryTimeFromAfterToWithValues", {
        from: startLabel,
        to: endLabel,
      })
    );
  }

  return errors;
}

export function hasValidSlotTimeRange(slot) {
  const startSeconds = timeToSeconds(slot.start_time);
  const endSeconds = timeToSeconds(slot.end_time);

  return (
    startSeconds !== null && endSeconds !== null && startSeconds < endSeconds
  );
}

export function buildSchedulesFromApi(
  startDate,
  endDate,
  type,
  timeSlots = [],
  excludedDates = [],
  legacyFestival = null
) {
  const schedules = syncSchedules([], startDate, endDate, type);
  const excludedSet = new Set(
    (excludedDates || []).map((date) => formatDateForApi(date))
  );
  const slotsByDate = {};

  (timeSlots || []).forEach((slot) => {
    const date = slot.slot_date
      ? formatDateForApi(slot.slot_date)
      : formatDateForApi(startDate);
    if (!slotsByDate[date]) slotsByDate[date] = [];
    slotsByDate[date].push({
      id: slot.id ?? null,
      start_time: parseTimeFromApi(slot.start_time),
      end_time: parseTimeFromApi(slot.end_time),
      max_tickets: slot.max_tickets ?? null,
    });
  });

  const hasApiSlots = Object.keys(slotsByDate).length > 0;

  return schedules.map((day) => {
    const isExcluded = excludedSet.has(day.date);
    const daySlots = slotsByDate[day.date];

    if (daySlots?.length > 0) {
      return {
        date: day.date,
        is_active: !isExcluded,
        time_slots: daySlots,
      };
    }

    if (
      !hasApiSlots &&
      legacyFestival?.start_time &&
      legacyFestival?.end_time &&
      day.date === formatDateForApi(startDate)
    ) {
      return {
        date: day.date,
        is_active: !isExcluded,
        time_slots: [
          {
            id: null,
            start_time: parseTimeFromApi(legacyFestival.start_time),
            end_time: parseTimeFromApi(legacyFestival.end_time),
            max_tickets: legacyFestival.max_tickets_count ?? null,
          },
        ],
      };
    }

    return {
      date: day.date,
      is_active: !isExcluded,
      time_slots: isExcluded ? [] : [createDefaultSlot()],
    };
  });
}

export function validateSchedules(schedules, translate) {
  const activeDays = (schedules || []).filter((day) => day.is_active);

  if (!activeDays.length) {
    return {
      message: translateMessage(translate, "MESSAGES.atLeastOneActiveDay"),
      dayDate: null,
      slotIndex: null,
    };
  }

  for (const day of activeDays) {
    const slots = day.time_slots || [];

    if (!slots.length) {
      return {
        message: translateMessage(
          translate,
          "MESSAGES.atLeastOneTimeSlotForDay",
          {
            date: formatDateForApi(day.date),
          }
        ),
        dayDate: day.date,
        slotIndex: null,
      };
    }

    const dayErrors = getDaySlotErrors(day, translate, {
      getSlotOptions: () => ({ showAll: true, touched: {} }),
    });
    const firstSlotIndex = Object.keys(dayErrors).sort(
      (a, b) => Number(a) - Number(b)
    )[0];

    if (firstSlotIndex !== undefined) {
      return {
        message: dayErrors[firstSlotIndex][0],
        dayDate: day.date,
        slotIndex: Number(firstSlotIndex),
      };
    }
  }

  return null;
}

export function getSlotTimeErrors(slot, translate, options = {}) {
  return getSlotFieldErrors(slot, translate, options);
}

export function getDaySlotErrors(
  day,
  translate,
  { getSlotOptions = () => ({ showAll: true, touched: {} }) } = {}
) {
  const errorsBySlot = {};
  const slots = day.time_slots || [];

  slots.forEach((slot, slotIndex) => {
    const errors = getSlotFieldErrors(
      slot,
      translate,
      getSlotOptions(slotIndex)
    );
    if (errors.length) {
      errorsBySlot[slotIndex] = errors;
    }
  });

  const sortedEntries = slots
    .map((slot, index) => ({ slot, index }))
    .filter(({ slot }) => hasValidSlotTimeRange(slot))
    .sort(
      (a, b) =>
        timeToSeconds(a.slot.start_time) - timeToSeconds(b.slot.start_time)
    );

  for (let index = 1; index < sortedEntries.length; index += 1) {
    const current = sortedEntries[index];
    const previous = sortedEntries[index - 1];
    const currentOptions = getSlotOptions(current.index);
    const shouldValidateOverlap =
      currentOptions.showAll ||
      currentOptions.touched.start_time ||
      currentOptions.touched.end_time;

    if (!shouldValidateOverlap) continue;

    const startSeconds = timeToSeconds(current.slot.start_time);
    const previousEnd = timeToSeconds(previous.slot.end_time);
    const overlapMessage = translateMessage(
      translate,
      "MESSAGES.timeSlotOverlapWithValues",
      {
        startTime: formatTimeForApi(current.slot.start_time),
        previousEndTime: formatTimeForApi(previous.slot.end_time),
      }
    );

    if (startSeconds <= previousEnd) {
      if (!errorsBySlot[current.index]) {
        errorsBySlot[current.index] = [];
      }

      if (!errorsBySlot[current.index].includes(overlapMessage)) {
        errorsBySlot[current.index].push(overlapMessage);
      }
    }
  }

  return errorsBySlot;
}

export function validateFestivalSchedulePrerequisites(
  { type, startDate, endDate },
  translate
) {
  if (!type?.id) {
    return {
      message: translate("MESSAGES.festivalTypeRequired"),
    };
  }

  if (!String(startDate ?? "").trim()) {
    return {
      message: translate("VALIDATION.startDate"),
    };
  }

  if (type.id === "extended" && !String(endDate ?? "").trim()) {
    return {
      message: translate("VALIDATION.endDate"),
    };
  }

  if (type.id === "extended" && endDate && startDate) {
    const start = moment(formatDateForApi(startDate), "YYYY-MM-DD", true);
    const end = moment(formatDateForApi(endDate), "YYYY-MM-DD", true);

    if (start.isValid() && end.isValid() && end.isBefore(start, "day")) {
      return {
        message: translate("VALIDATION.handle"),
      };
    }
  }

  return null;
}

export function appendScheduleToFormData(formData, schedules, ticketType) {
  let slotIndex = 0;
  const excludedDates = [];

  (schedules || []).forEach((day) => {
    const apiDate = formatDateForApi(day.date);

    if (!day.is_active) {
      excludedDates.push(apiDate);
      return;
    }

    day.time_slots.forEach((slot) => {
      formData.append(
        `time_slots[${slotIndex}][start_time]`,
        formatTimeForApi(slot.start_time)
      );
      formData.append(
        `time_slots[${slotIndex}][end_time]`,
        formatTimeForApi(slot.end_time)
      );
      formData.append(
        `time_slots[${slotIndex}][max_tickets]`,
        slot.max_tickets ?? ""
      );
      formData.append(`time_slots[${slotIndex}][slot_date]`, apiDate);
      formData.append(`time_slots[${slotIndex}][is_active]`, 1);
      if (slot.id) {
        formData.append(`time_slots[${slotIndex}][id]`, slot.id);
      }
      slotIndex += 1;
    });
  });

  excludedDates.forEach((date, index) => {
    formData.append(`excluded_dates[${index}]`, date);
  });

  formData.append("ticket_type", ticketType ?? "");
}

export function isRichTextEmpty(value) {
  if (!value) return true;

  const text = String(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

  return !text;
}

export function validateUsagePolicyFields(
  usagePolicyAr,
  usagePolicyEn,
  translate
) {
  if (isRichTextEmpty(usagePolicyAr)) {
    return translate("VALIDATION.usagePolicyArRequired");
  }

  if (isRichTextEmpty(usagePolicyEn)) {
    return translate("VALIDATION.usagePolicyEnRequired");
  }

  return null;
}

export function formatFestivalApiErrorMessage(message) {
  if (!message) return message;

  return String(message)
    .replace(/سياسة الاستخدام/g, "شروط الاستخدام")
    .replace(/سياسة الاستخدم/g, "شروط الاستخدام")
    .replace(/usage policy/gi, "terms of use");
}

export function getFestivalApiErrorMessage(error) {
  const data = error?.response?.data;
  if (!data) return null;

  const rawMessage =
    data.message ||
    (Array.isArray(data.errors?.message)
      ? data.errors.message[0]
      : data.errors?.message);

  return formatFestivalApiErrorMessage(rawMessage);
}
